// dnd-kit version of ImagesInput with full support for grid layout and all features

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import { useRef, useLayoutEffect, useState } from 'react';
import { CSS } from '@dnd-kit/utilities';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import './ImagesInput.css';



function ImagesInput({
  name,
  label,
  hint,
  error,
  addBtnText,
  changeBtnText = 'Изменить...',
  images = [],
  onRemove,
  onAdd,
  onSort,
  isMultiple = false,
  isHorizontal = false,
  labelClassName,
  isPreloaderVisible,
  additionalKey = '',
  readOnly,
  isDNDActive,
  videoAcepted,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = images.findIndex((_, i) => `sortable-${i}` === active.id);
      const newIndex = images.findIndex((_, i) => `sortable-${i}` === over?.id);
      const newItems = arrayMove(images, oldIndex, newIndex);
      onSort?.({ target: { value: newItems } });
    }
  };

  function SortableItem({ id, index, item, isHorizontal, onRemove, readOnly }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id });

    const elementRef = useRef(null);
    const [itemSize, setItemSize] = useState({ width: undefined, height: undefined });

    useLayoutEffect(() => {
      if (elementRef.current) {
        const { width, height } = elementRef.current.getBoundingClientRect();
        setItemSize({ width, height });
      }
    }, [images.length]); // re-measure when list changes

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      userSelect: 'none',
      opacity: isDragging ? 0.5 : 1,
      width: itemSize.width,
      height: itemSize.height,
      boxSizing: 'border-box'
    };

    const url = item.urls?.orig;
    const isVideo = item.metadata?.first_frame_db;
    const videoPreview = isVideo ? item.metadata.first_frame_db.urls.orig : null;

    return (
      <div
        ref={(node) => {
          setNodeRef(node);
          elementRef.current = node;
        }}
        {...attributes}
        {...listeners}
        className={`images-input__img-box ${isHorizontal ? 'images-input__img-box_horizontal' : ''}`}
        style={style}
      >
        {!readOnly && (
          <button
            className="images-input__remove-btn"
            type="button"
            aria-label="Удалить файл"
            onClick={() => onRemove({ target: { value: index } })}
          >
            ×
          </button>
        )}

        {isVideo ? (
          <div className="images-input__video-wrapper">
            <img className="images-input__img" src={videoPreview} alt="" />
            <span className="images-input__play-icon">▶</span>
          </div>
        ) : (
          <img className="images-input__img" src={url} alt="" />
        )}
      </div>
    );
  }

  return (
    <div className="images-input">
      {(label || hint) && (
        <div className="images-input__heading">
          {label && <span className={`images-input__label ${labelClassName || ''}`}>{label}</span>}
          {error ? (
            <span className="images-input__error" dangerouslySetInnerHTML={{ __html: error }} />
          ) : hint && !readOnly ? (
            <span className="images-input__hint" dangerouslySetInnerHTML={{ __html: hint }} />
          ) : null}
        </div>
      )}

      <div className="images-input__input-box">
        {images && images.length > 0 ?
          onSort && isDNDActive ?
            (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={images.map((_, i) => `sortable-${i}`)}
                  strategy={rectSortingStrategy}
                >
                  <div className="images-input__list">
                    {images.map((item, i) => (
                      <SortableItem
                        key={`sortable-${i}`}
                        id={`sortable-${i}`}
                        index={i}
                        item={item}
                        onRemove={onRemove}
                        readOnly={readOnly}
                        isHorizontal={isHorizontal}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )
            :
            (
              <div className="images-input__list">
                {images.map((item, i) => {
                  const url = item.urls?.orig;
                  const isVideo = item.metadata?.first_frame_db;
                  const videoPreview = isVideo ? item.metadata.first_frame_db.urls.orig : null;
                  return (
                    <div
                      className={`images-input__img-box ${isHorizontal ? 'images-input__img-box_horizontal' : ''}`}
                    >
                      {!readOnly && (
                        <button
                          className="images-input__remove-btn"
                          type="button"
                          aria-label="Удалить файл"
                          onClick={() => onRemove({ target: { value: i } })}
                        >
                          ×
                        </button>
                      )}

                      {isVideo ? (
                        <div className="images-input__video-wrapper">
                          <img className="images-input__img" src={videoPreview} alt="" />
                          <span className="images-input__play-icon">▶</span>
                        </div>
                      ) : (
                        <img className="images-input__img" src={url} alt="" />
                      )}
                    </div>
                  )
                })}
              </div>
            )
          : readOnly ? (
            <label className="images-input__read-only-placeholder">-</label>
          ) : null}

        {!readOnly && (
          <label
            className={`images-input__add-btn ${isPreloaderVisible ? 'images-input__add-btn_disabled' : ''}`}
            htmlFor={name + additionalKey}
          >
            <input
              className="images-input__input"
              id={name + additionalKey}
              name={name}
              type="file"
              accept={videoAcepted ? "image/jpg, image/jpeg, image/webp, image/png, image/heic, video/mp4, video/quicktime, video/x-msvideo, video/x-matroska" : "image/jpg, image/jpeg, image/webp, image/png, image/heic"}
              multiple={isMultiple}
              onChange={onAdd}
            />
            <span
              className={`images-input__add-btn-text ${!isPreloaderVisible ? 'images-input__add-btn-text_visible' : ''}`}
            >
              {isMultiple ? addBtnText : images && images.length > 0 ? changeBtnText : addBtnText}
            </span>
            <span
              className={`images-input__preloader ${isPreloaderVisible ? 'images-input__preloader_visible' : ''}`}
            >
              <MiniPreloader />
            </span>
          </label>
        )}
      </div>
    </div>
  );
}

export default ImagesInput;